import { useContext, useEffect, useState } from 'react';
import { Button, Col, Row, Switch } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { NotificationContext } from '../../providers/NotificationProvider';
import { deleteLeadEntry, getLeadEntries, updateLeadEntry } from '../../features/user/userSlice';
import DataTable from '../shared/DataTable';
import { useTranslation } from 'react-i18next';
import { InformationLeadType, TrialLeadType } from '../../types/types';

const LeadEntries: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { triggerNotification } = useContext(NotificationContext);
  const { formKey = '' } = useParams();
  const [title, setTitle] = useState('');
  const [entries, setEntries] = useState<(TrialLeadType | InformationLeadType)[]>([]);

  const loadEntries = () => {
    if (!formKey) {
      return;
    }

    dispatch(getLeadEntries(formKey) as any)
      .then((result: any) => {
        if (getLeadEntries.fulfilled.match(result)) {
          setTitle(result.payload?.name || '');
          setEntries(result.payload?.entries || []);
          return;
        }

        triggerNotification('error', t('Error'), result.payload?.message || t('No se pudieron cargar los leads.'), 'bottomRight');
      })
      .catch((error: any) => {
        triggerNotification('error', t('Error'), error.message || t('No se pudieron cargar los leads.'), 'bottomRight');
      });
  };

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, formKey, t, triggerNotification]);

  const handleToggleContacted = (item: TrialLeadType | InformationLeadType, contacted: boolean) => {
    dispatch(updateLeadEntry({ formKey, leadId: item.id, contacted }) as any)
      .then((result: any) => {
        if (updateLeadEntry.fulfilled.match(result)) {
          triggerNotification('success', t('Éxito'), result.payload?.message || t('Lead actualizado correctamente.'), 'bottomRight');
          loadEntries();
          return;
        }

        triggerNotification('error', t('Error'), result.payload?.message || t('No se pudo actualizar el lead.'), 'bottomRight');
      })
      .catch((error: any) => {
        triggerNotification('error', t('Error'), error.message || t('No se pudo actualizar el lead.'), 'bottomRight');
      });
  };

  const handleDelete = (item: TrialLeadType | InformationLeadType) => {
    if (!window.confirm(t('¿Está seguro de eliminar este lead?'))) {
      return;
    }

    dispatch(deleteLeadEntry({ formKey, leadId: item.id }) as any)
      .then((result: any) => {
        if (deleteLeadEntry.fulfilled.match(result)) {
          triggerNotification('success', t('Éxito'), t('Lead eliminado correctamente.'), 'bottomRight');
          loadEntries();
          return;
        }

        triggerNotification('error', t('Error'), result.payload?.message || t('No se pudo eliminar el lead.'), 'bottomRight');
      })
      .catch((error: any) => {
        triggerNotification('error', t('Error'), error.message || t('No se pudo eliminar el lead.'), 'bottomRight');
      });
  };

  const formatCreatedAt = (value: string) => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('es-CO', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const contactedColumn = {
    title: t('Contactado'),
    dataIndex: 'contacted',
    key: 'contacted',
    render: (value: boolean, item: TrialLeadType | InformationLeadType) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <Switch
          checked={value}
          checkedChildren={t('Sí')}
          unCheckedChildren={t('No')}
          onChange={(checked) => handleToggleContacted(item, checked)}
        />
        <strong>{value ? t('Sí') : t('No')}</strong>
      </span>
    ),
  };

  const actionColumn = {
    title: t('Acciones'),
    key: 'actions',
    render: (_: unknown, item: TrialLeadType | InformationLeadType) => (
      <span className='action-group'>
        <button className='naked' type='button' title={t('Eliminar')} onClick={() => handleDelete(item)}>
          <DeleteOutlined />
        </button>
      </span>
    ),
  };

  const trialColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: t('Nombre'), dataIndex: 'first_name', key: 'first_name', searchable: true },
    { title: t('Apellido'), dataIndex: 'last_name', key: 'last_name', searchable: true },
    { title: t('Correo electrónico'), dataIndex: 'email', key: 'email', searchable: true },
    { title: t('Indicativo'), dataIndex: 'country_code', key: 'country_code' },
    { title: t('Teléfono'), dataIndex: 'phone', key: 'phone', searchable: true },
    { title: t('Empresa'), dataIndex: 'company', key: 'company', searchable: true },
    contactedColumn,
    { title: t('Estado'), dataIndex: 'status', key: 'status' },
    { title: t('Fecha de Creación'), dataIndex: 'created_at', key: 'created_at', render: formatCreatedAt },
    actionColumn,
  ];

  const informationColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: t('Nombre'), dataIndex: 'name', key: 'name', searchable: true },
    { title: t('Correo electrónico'), dataIndex: 'email', key: 'email', searchable: true },
    { title: t('Teléfono'), dataIndex: 'phone', key: 'phone', searchable: true },
    { title: t('Empresa'), dataIndex: 'company', key: 'company', searchable: true },
    {
      title: t('Curso Virtual de Interés'),
      dataIndex: 'virtual_course_interests',
      key: 'virtual_course_interests',
      searchable: true,
      render: (value: string[] | undefined) => (value && value.length ? value.join(', ') : ''),
    },
    { title: t('Tema de interés'), dataIndex: 'interest_topic', key: 'interest_topic', searchable: true },
    { title: t('Origen'), dataIndex: 'source', key: 'source', searchable: true },
    contactedColumn,
    { title: t('Fecha de Creación'), dataIndex: 'created_at', key: 'created_at', render: formatCreatedAt },
    actionColumn,
  ];

  return (
    <Row className='content'>
      <Col style={{ width: '100%' }}>
        <div className='title-full'>
          <h1>{title || t('Leads')}</h1>
          <Button onClick={() => navigate('/leads')}>{t('Volver')}</Button>
        </div>
        <div className='ant-divider'></div>
        <DataTable
          dataSource={entries}
          columns={formKey === 'request-trial' ? trialColumns : informationColumns}
        />
      </Col>
    </Row>
  );
};

export default LeadEntries;
